import {CommonModule} from "@angular/common";
import {Component, Inject} from "@angular/core";
import {MatButtonModule} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {Observable} from "rxjs";
import {
  IPushJobStatus,
  PushJobKind,
  pushJobNoun,
} from "../../../core/api/controllers/push-jobs/dto/push-job";
import {NotificationService} from "../../../core/services/notification.service";
import {ConfirmationDialogComponent} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";

export interface IPushProgressDialogData {
  kind: PushJobKind;
  job$: Observable<IPushJobStatus | null>;
  cancel: () => Observable<IPushJobStatus>;
  retry: () => Observable<IPushJobStatus>;
  downloadLog: () => Observable<Blob>;
  acknowledge: () => Observable<void>;
}

@Component({
  selector: "app-push-progress-dialog",
  standalone: true,
  templateUrl: "./push-progress-dialog.component.html",
  styleUrls: ["./push-progress-dialog.component.scss"],
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
  ],
})
export class PushProgressDialogComponent {
  actionInProgress = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IPushProgressDialogData,
    private dialogRef: MatDialogRef<PushProgressDialogComponent>,
    private dialog: MatDialog,
    private notifications: NotificationService,
  ) {}

  get noun(): string {
    return pushJobNoun(this.data.kind);
  }

  minimize(): void {
    this.dialogRef.close();
  }

  cancel(job: IPushJobStatus): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Cancel remaining locations?",
        message: "The location currently being processed will finish. All remaining locations will be skipped.",
        confirmButtonLabel: "Cancel remaining",
        cancelButtonLabel: "Keep running",
      },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.actionInProgress = true;
      this.data.cancel().subscribe({
        next: () => this.actionInProgress = false,
        error: (error) => {
          this.actionInProgress = false;
          this.notifications.error(error);
        },
      });
    });
  }

  retry(): void {
    this.actionInProgress = true;
    this.data.retry().subscribe({
      next: () => this.actionInProgress = false,
      error: (error) => {
        this.actionInProgress = false;
        this.notifications.error(error);
      },
    });
  }

  downloadLog(job: IPushJobStatus): void {
    this.actionInProgress = true;
    this.data.downloadLog().subscribe({
      next: (log) => {
        const url = window.URL.createObjectURL(log);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.noun}-push-${job.id}.log`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.actionInProgress = false;
      },
      error: (error) => {
        this.actionInProgress = false;
        this.notifications.error(error);
      },
    });
  }

  dismiss(): void {
    this.actionInProgress = true;
    this.data.acknowledge().subscribe({
      next: () => this.dialogRef.close(),
      error: (error) => {
        this.actionInProgress = false;
        this.notifications.error(error);
      },
    });
  }

  statusLabel(job: IPushJobStatus): string {
    const noun = this.titleCase(this.noun);
    switch (job.status) {
      case "Completed": return `${noun} push completed`;
      case "CompletedWithFailures": return `${noun} push completed with issues`;
      case "Canceled": return `${noun} push canceled`;
      case "Interrupted": return `${noun} push interrupted by a server restart`;
      case "Failed": return `${noun} push failed`;
      case "CancelRequested": return "Cancel requested";
      default: return `Pushing ${this.noun}s`;
    }
  }

  objectSummary(job: IPushJobStatus): string {
    const objects = `${job.objectCount} ${this.noun}${job.objectCount === 1 ? "" : "s"}`;
    const locations = `${job.totalLocations} location${job.totalLocations === 1 ? "" : "s"}`;
    return `${objects} · ${locations}`;
  }

  isTerminal(job: IPushJobStatus): boolean {
    return ["Completed", "CompletedWithFailures", "Canceled", "Interrupted", "Failed"]
      .includes(job.status);
  }

  private titleCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
